import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  HintCoverageAccountType,
  UpsertHintRegionSettingBody,
  listHintRegionSettings,
  upsertHintRegionSetting,
} from '~/services/ConnectionGoogleService';

import { hintRegionCompositionQueryKeys } from './useHintRegionComposition';

export const hintRegionSettingsQueryKeys = {
  all: ['hintRegionSettings'] as const,
  list: (accountType: HintCoverageAccountType) => [...hintRegionSettingsQueryKeys.all, accountType] as const,
};

export function useHintRegionSettings(accountType: HintCoverageAccountType) {
  return useQuery({
    queryKey: hintRegionSettingsQueryKeys.list(accountType),
    queryFn: async ({ signal }) => {
      const response = await listHintRegionSettings(accountType, { signal });
      return response.result;
    },
    enabled: !!accountType,
  });
}

export function useUpsertHintRegionSetting() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (body: UpsertHintRegionSettingBody) => {
      return await upsertHintRegionSetting(body);
    },
    onSuccess: (_response, body) => {
      queryClient.invalidateQueries({ queryKey: hintRegionSettingsQueryKeys.list(body.accountType) });
      // The composition endpoint applies the CURRENT weights to stored counts, so a
      // saved weight/enabled change shifts its numbers immediately. Property scores
      // and tiers only move at the next reclassification run.
      queryClient.invalidateQueries({ queryKey: hintRegionCompositionQueryKeys.composition(body.accountType) });
      enqueueSnackbar('Region setting saved — scores apply at the next reclassification', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to save region setting: ${error.message || 'Unknown error'}`, { variant: 'error' });
    },
  });
}
